import axios from "axios";
import { supabase } from "../supabaseClient";

// CRM 2.0 booking card lists (M1-F10). Server: GET /crm2/list/:tab
// tab = application-for-booking | agreement-preparation | agreement-in-process | agreement-complete | cancellations
// Returns { subTabs, counts, late, emailNotSent, waitingForSanction, cards }
export const CRM2_LIST_TAB = {
  "Application for Booking": "application-for-booking",
  "Agreement Preparation": "agreement-preparation",
  "Agreement in Process": "agreement-in-process",
  "Agreement Complete": "agreement-complete",
  Cancellations: "cancellations",
};

// Today's CRM list functions, most likely first for a crmStatusId. They overlap
// (e.g. tag approved also returns 3), so the card ⋮ menu tries them in order.
const OLD_CRM_RPCS = {
  0: ["get_tag_pending_crm_allotments"],
  1: ["get_tag_approved_crm_allotments"],
  3: ["get_changed_plan_crm_allotments", "get_tag_approved_crm_allotments"],
  4: ["get_parking_selection_crm_allotments"],
  2: ["get_update_bucketing_crm_allotments_new"],
  5: ["get_agreement_done_crm_allotments_test"],
  6: ["get_completed_crm_allotment_test"],
};
const ALL_OLD_CRM_RPCS = [...new Set([...Object.values(OLD_CRM_RPCS).flat(), "get_cancelled_and_refund_allotment"])];

// M1-F11 (M1.28): the card ⋮ menu reuses today's pop-ups, which read the booking in
// the shape of today's CRM lists (lead_name, unit_no, allotment_payment_id …).
export async function getOldCrmRow(card) {
  const id = card?.allotmentPaymentId;
  if (!id) throw new Error("No booking id");
  const first = card?.cancel ? ["get_cancelled_and_refund_allotment"] : OLD_CRM_RPCS[card?.crmStatusId] || [];
  for (const rpc of [...first, ...ALL_OLD_CRM_RPCS.filter((r) => !first.includes(r))]) {
    const { data, error } = await supabase.rpc(rpc, { search_lead_name: "" }).eq("allotment_payment_id", id).limit(1);
    if (error) {
      console.warn("[crm2][getOldCrmRow] list read failed, trying the next", { rpc, id, error: error.message });
      continue;
    }
    if (data?.length) {
      console.log("[crm2][getOldCrmRow] found", { rpc, id });
      return data[0];
    }
  }
  console.warn("[crm2][getOldCrmRow] booking not in any of today's CRM lists", { id, crmStatusId: card?.crmStatusId });
  return null;
}

export async function getCrm2List(topTab) {
  const tab = CRM2_LIST_TAB[topTab];
  if (!tab) throw new Error(`No CRM 2.0 list for ${topTab}`);
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/list/${tab}`;
  const res = await axios.get(url, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } });
  return res.data;
}

// M2-F2: Application for Booking email of a CRM 2.0 booking. With email: a test send to
// that address only. Returns { ok, to, cc, crmHeadCc, attachments } or throws (message in
// err.response.data.message).
export async function sendCrm2ApplicationEmail(allotmentPaymentId, email = null) {
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/bookings/${allotmentPaymentId}/application-email`;
  const res = await axios.post(url, email ? { email } : {}, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } });
  return res.data;
}

// M2-F6: Accept / Reject a Tag Pending CRM 2.0 booking (tagged CRM, CRM Head or Super Admin).
// Accept → { status: "ACCEPTED", email, whatsapp }; Reject → { status: "PASSED_ON" | "UNCLAIMED", nextCrmName }.
// Errors throw (message in err.response.data.message).
export async function crm2AcceptTag(allotmentPaymentId) {
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/tag/accept`;
  return (await axios.post(url, { allotmentPaymentId }, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } })).data;
}
export async function crm2RejectTag(allotmentPaymentId, reason) {
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/tag/reject`;
  return (await axios.post(url, { allotmentPaymentId, reason }, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } })).data;
}

// M2-F7: claim an Unclaimed CRM 2.0 booking (first CRM wins; 409 "already claimed").
export async function crm2Claim(allotmentPaymentId) {
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/claim`;
  return (await axios.post(url, { allotmentPaymentId }, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } })).data;
}

// M4-F9: CRM Head removes the loyalty discount. preview true → { now, after, increase, points, showDefault };
// otherwise { moveTo: "REFERRAL" | "DIRECT", reason, showToCustomer } applies it. Errors throw (err.response.data.message).
export async function crm2RemoveLoyalty(allotmentPaymentId, body) {
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/loyalty/remove`;
  return (await axios.post(url, { allotmentPaymentId, ...body }, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } })).data;
}

// M2-F8 · Reassign CRM (CRM Head): the project's CRMs for the pop-up, then the move. Errors throw (err.response.data.message).
export async function crm2ReassignOptions(allotmentPaymentId) {
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/reassign/options/${allotmentPaymentId}`;
  return (await axios.get(url, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } })).data?.data;
}
export async function crm2Reassign(allotmentPaymentId, { crmUserId, reason, emailCustomer }) {
  const url = `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/reassign`;
  return (await axios.post(url, { allotmentPaymentId, crmUserId, reason, emailCustomer }, { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } })).data;
}

// ---------- M5 · AV and payment plan, parking, booking amount, Change Unit (server steps; errors throw, err.response.data.message)
const m5Url = (path) => `${process.env.REACT_APP_PAYMENT_LINK_URL}crm2/${path}`;
const m5Auth = () => ({ headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } });
const m5Get = async (path) => (await axios.get(m5Url(path), m5Auth())).data?.data;
const m5Post = async (path, body) => (await axios.post(m5Url(path), body, m5Auth())).data?.data;

// M5-F2: CRM Review › Preview and Approve → { status: PLAN_APPROVED | OCR_PENDING | AV_PENDING, … }
export const crm2AvSubmit = (allotmentPaymentId, { newBsp, plan, remark, skip, parkingUpgrade = 0 }) => m5Post("av/submit", { allotmentPaymentId, newBsp, plan, remark, skip, parkingUpgrade });
// M5-F2: Approval Pending decision (AV change approve / reject, OCR approve)
export const crm2DecideApproval = (crm2ApprovalId, { decision, newBsp = null, gstPercent = null, remark = "", parkingUpgrade = null }) => m5Post(`approvals/${crm2ApprovalId}/decide`, { decision, newBsp, gstPercent, remark, parkingUpgrade });
export const crm2MoveToCrmReview = (allotmentPaymentId) => m5Post("move-to-crm-review", { allotmentPaymentId });
// M5-F8
export const crm2ChangeBookingAmount = (allotmentPaymentId, { ocr, sdr }) => m5Post("booking-amount", { allotmentPaymentId, ocr, sdr });
// M5-F5: { flow, applicable, why, rules: { maxIncluded, maxUpgrade }, included, upgrades, current, upgradeCost }
export const crm2ParkingOptions = (allotmentPaymentId) => m5Get(`parking/options/${allotmentPaymentId}`);
export const crm2ParkingSelect = (allotmentPaymentId, { includedIds, upgradeIds, preview = false }) => m5Post("parking/select", { allotmentPaymentId, includedIds, upgradeIds, preview });
export const crm2ParkingSkip = (allotmentPaymentId) => m5Post("parking/skip", { allotmentPaymentId });
// M5-F9: { current, units, pending }
export const crm2ChangeUnitUnits = (allotmentPaymentId) => m5Get(`change-unit/${allotmentPaymentId}/units`);
export const crm2ChangeUnitRaise = (allotmentPaymentId, { unitId, paymentPlanId, reason, plan = null }) => m5Post("change-unit/raise", { allotmentPaymentId, unitId, paymentPlanId, reason, plan });
export const crm2ChangeUnitDecide = (changeUnitRequestId, { decision, remark }) => m5Post(`change-unit/${changeUnitRequestId}/decide`, { decision, remark });

// ---------- M7 · Final Review (server approve) and cancellations (errors throw, err.response.data.message)
// Final Review pop-up: { blocks, declaration, checks, letter, sections }
export const crm2FinalReview = (allotmentPaymentId) => m5Get(`final-review/${allotmentPaymentId}`);
export const crm2FinalReviewApprove = (allotmentPaymentId, { checklist, declaration }) => m5Post(`final-review/${allotmentPaymentId}/approve`, { checklist, declaration });
export const crm2KycSendBack = (allotmentPaymentId, reason) => m5Post(`final-review/${allotmentPaymentId}/kyc-send-back`, { reason });
export const crm2AfbReminder = (allotmentPaymentId) => m5Post(`afb/${allotmentPaymentId}/reminder`, {});
// cancellations: reasons (CANCEL_RAISE / CANCEL_EXTEND) + settings
export const crm2CancelReasons = (group) => m5Get(`cancellations/reasons/${group}`);
export const crm2CancelRaise = (allotmentPaymentId, { reasonCode, details }) => m5Post("cancellations", { allotmentPaymentId, reasonCode, details });
export const crm2CancelPayments = (cancelUnitRequestId) => m5Get(`cancellations/${cancelUnitRequestId}/payments`);
export const crm2CancelWithdraw = (cancelUnitRequestId) => m5Post(`cancellations/${cancelUnitRequestId}/withdraw`, {});
export const crm2CancelReminder = (cancelUnitRequestId) => m5Post(`cancellations/${cancelUnitRequestId}/reminder`, {});
export const crm2CancelDecide = (cancelUnitRequestId, { action, mode, reason }) => m5Post(`cancellations/${cancelUnitRequestId}/decide`, { action, mode, reason });
export const crm2CancelHeadDecide = (cancelUnitRequestId, { action, reason }) => m5Post(`cancellations/${cancelUnitRequestId}/head-decide`, { action, reason });
export const crm2CancelExtend = async (cancelUnitRequestId, { days, reasonCode, note, file }) => {
  const fd = new FormData();
  fd.append("days", String(days));
  fd.append("reasonCode", reasonCode);
  if (note) fd.append("note", note);
  if (file) fd.append("document", file);
  return (await axios.post(m5Url(`cancellations/${cancelUnitRequestId}/extend`), fd, m5Auth())).data?.data;
};
export const crm2RefundInitiate = (cancelUnitRequestId) => m5Post(`cancellations/${cancelUnitRequestId}/refund/initiate`, {});
export const crm2RefundRelease = (cancelUnitRequestId) => m5Post(`cancellations/${cancelUnitRequestId}/refund/release`, {});
export const crm2Reprice = (cancelUnitRequestId, { bsp, gst, sdr, total, skip = false }) => m5Post(`cancellations/${cancelUnitRequestId}/reprice`, { bsp, gst, sdr, total, skip });
// M8: Allotment Letter pop-up (letter state, applicants, eSign attempts, send log), 10-minute link, Send reminder
export const crm2AllotmentLetter = (allotmentPaymentId) => m5Get(`allotment-letter/${allotmentPaymentId}`);
export const crm2AllotmentLetterLink = (allotmentPaymentId, which = "letter") => m5Get(`allotment-letter/${allotmentPaymentId}/link?which=${which}`);
export const crm2AllotmentLetterReminder = (allotmentPaymentId) => m5Post(`allotment-letter/${allotmentPaymentId}/reminder`, {});
// M12's Make letter again after the last failed try (routes/templates.js, mounted at the root)
export const crm2MakeLetterAgain = async (allotmentPaymentId) =>
  (await axios.post(`${process.env.REACT_APP_PAYMENT_LINK_URL}allotment-letter/${allotmentPaymentId}/make-again`, {}, m5Auth())).data?.data;
